/* global globalThis */
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ForgotPassword.scss";
import { LanguageSelect } from "../../pages/LanguageSelect";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Grid, Input, InputLabel, Link, Select, MenuItem, Snackbar, SnackbarContent, makeStyles } from "@material-ui/core";
import {
  useNetworkStatus,
  useTranslations,
} from "sdk/custom-hooks";
import i18n from "sdk/translations";
import { forgotPassword } from "sdk/actions/getUser";
import LearnifyIcon from "../../assets/images/learnifyDarkLogo.svg";
import { ColorsConstant } from "../../constants/colorConstant";
import {
  getSettingsAction,
  setSubdomain,
} from "sdk/actions/getSettingsAction";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";

const isValidEmailFormat = (value = "") => {
  if (typeof value !== "string" || value.length > 254) {
    return false;
  }

  const atIndex = value.indexOf("@");
  if (atIndex <= 0 || atIndex !== value.lastIndexOf("@") || atIndex === value.length - 1) {
    return false;
  }

  const local = value.slice(0, atIndex);
  const domain = value.slice(atIndex + 1);
  if (local.length > 64 || domain.length > 253) {
    return false;
  }

  if (domain.startsWith(".") || domain.endsWith(".") || domain.includes("..")) {
    return false;
  }

  const domainParts = domain.split(".");
  if (domainParts.length < 2) {
    return false;
  }

  const localPattern = /^[A-Za-z0-9.!#$%&'*+/=?^_{|}~-]+$/;
  const labelPattern = /^[A-Za-z0-9-]+$/;

  return (
    localPattern.test(local) &&
    domainParts.every(
      (part) =>
        part.length > 0 &&
        part.length <= 63 &&
        !part.startsWith("-") &&
        !part.endsWith("-") &&
        labelPattern.test(part)
    )
  );
};

const useStyles = makeStyles(() => ({
  onlineError: {
    display: "block",
  },
  forgotTitle: (props) => ({
    color: props.bgColor,
  }),
  codeLabel: (props) => ({
    color: props.bgColor,
    padding: "2px",
  }),
  selectFullWidth: {
    width: "100%",
  },
  inputLabelColor: (props) => ({
    color: props.bgColor,
  }),
  submitButton: (props) => ({
    backgroundColor: props.bgColor,
    color: props.textColor,
  }),
  backLoginLink: (props) => ({
    color: props.bgColor,
  }),
}));

const ForgotPassword = () => {
  const changePasswordBtnRef = useRef(null);
  const history = useHistory();
  const [bgColor, setBgColor] = useState(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [textColor, setTextColor] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dynamicLogo, setDynamicLogo] = useState(null);
  const [settingsData, setSettingsData] = useState([]);
  const [showCountryCode, setShowCountryCode] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");
  const [countryCodes, setCountryCodes] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("error");
  const isOnline = useNetworkStatus();
  const dispatch = useDispatch();
  const t = useTranslations();
  const classes = useStyles({ bgColor, textColor });
  const [subDomainName, setSubDomainName] = useState("");
  const language =
    useSelector((state) => state.getLanguaggeReducer.lang) || "en";

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  const showSnackbar = useCallback((message, type = "error") => {
    if (!message) {
      return;
    }
    setSnackbarType(type);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    // Load country codes
    const countries = getCountries();
    const codes = countries.map(code => {
      const callingCode = getCountryCallingCode(code);
      return {
        code: code,
        dialCode: `+${callingCode}`,
        display: `+${callingCode} (${code})`
      };
    }).sort((a, b) => a.dialCode.localeCompare(b.dialCode));
    setCountryCodes(codes);
  }, []);

  useEffect(() => {
    getSubdomain();
  }, []);

  useEffect(() => {
    const dynamicLogos =
      settingsData?.data?.[0]?.branding?.logos?.webAndAppLogo?.imagePath;
    setDynamicLogo(dynamicLogos || LearnifyIcon);
    setBgColor(
      settingsData?.data?.[0]
        ? settingsData?.data?.[0]?.branding?.backgroundAndTextTheme
          ?.primaryBackgroundColor
        : ColorsConstant.defaultBlue
    );
    setTextColor(
      settingsData?.data?.[0]
        ? settingsData?.data?.[0]?.branding?.backgroundAndTextTheme
          ?.primaryTextColor
        : ColorsConstant.white
    );
  }, [isLoading, settingsData]);

  const focusTextField = (fieldId) => {
    // check if we want it.
    const field = document.getElementById(fieldId);
    if (field) {
      field.focus();
    }
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);

    // Check if input contains only numbers
    const isNumeric = /^\d+$/.test(value);
    const shouldShowCode = isNumeric && value.length > 0;

    if (shouldShowCode !== showCountryCode) {
      setShowCountryCode(shouldShowCode);
      // Maintain focus after state change
      setTimeout(() => {
        document.getElementById("userForgotPassEmail")?.focus();
      }, 0);
    }

    const isMobileNumber = (str) => /^\d{10}$/.test(str);
    let errMsg = "";
    let emailValid = true;
    // Validate email or mobile number
    if (!isValidEmailFormat(value) && !isMobileNumber(value)) {
      errMsg = t("Please enter a valid email id or 10-digit mobile number.");
      emailValid = false;
    }
    // Handle error message and button state
    const errorElement = document.getElementById("reset-credential-error");
    if (emailValid) {
      errorElement.style.display = "none";
      if (changePasswordBtnRef.current) {
        changePasswordBtnRef.current.removeAttribute("disabled");
      }
      setIsButtonDisabled(false);
    } else {
      errorElement.style.display = "none";
      showSnackbar(errMsg, "error");
      if (changePasswordBtnRef.current) {
        changePasswordBtnRef.current.setAttribute("disabled", "disabled");
      }
      setIsButtonDisabled(true);
      focusTextField("userForgotPassEmail");
    }
  };

  const getSubdomain = () => {
    const host = globalThis.location.host;
    const domainParts = host.split(".");
    if (process.env.REACT_APP_TENANT_DOMAIN) {
      setSubDomainName(process.env.REACT_APP_TENANT_DOMAIN);
      dispatch(setSubdomain(process.env.REACT_APP_TENANT_DOMAIN));
    } else if (domainParts.length > 3) {
      const domainName = domainParts[0];
      setSubDomainName(domainName);
      dispatch(setSubdomain(domainName));
    }
  };

  const getSettingsApiData = useCallback(async () => {
    try {
      await dispatch(getSettingsAction(subDomainName)).then((res) => {
        setSettingsData(res?.data);
        setIsLoading(true);
      });
    } catch (error) {
      console.log("Error -", error.message);
    }
  }, [dispatch, subDomainName]);

  useEffect(() => {
    if (subDomainName && subDomainName !== null) {
      getSettingsApiData();
    }
  }, [subDomainName, getSettingsApiData]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    let forgotPasswordData = {
      [Number.isNaN(+email) ? "email" : "mobileNumber"]: email?.toLowerCase(),
      ...(!Number.isNaN(+email) && { countryCode }),
      subdomain: subDomainName,
      userLogin: true,
    };

    const isMobileNumber = (str) => /^\d{10}$/.test(str);
    const errorElement = document.getElementById("reset-credential-error");
    let inputType = null;

    if (isValidEmailFormat(email.toLowerCase())) {
      inputType = 'email';
    } else if (isMobileNumber(email)) {
      inputType = 'mobileNumber';
    }
    if (!inputType) {
      errorElement.style.display = "none";
      showSnackbar(t("Please enter a valid email id or 10-digit mobile number."), "error");
      return;
    }

    errorElement.style.display = "none";
    dispatch(forgotPassword(forgotPasswordData)).then((res) => {
      if (res.status === 200) {
        history.push("/verify-otp", { userIdentifier: email });
        errorElement.innerHTML = `<div class=" messageBox successMsg">${t(res.message)}</div>`;
        errorElement.style.display = "block";
        if (changePasswordBtnRef.current) {
          changePasswordBtnRef.current
            .getElementById("changeCredentialBtn")
            .setAttribute("disabled", "disabled");
        }
      } else {
        errorElement.style.display = "none";
        showSnackbar(t(res.message), "error");
      }
    });
  };

  return (
    <Grid className="forgot-password-parent-div">
      <LanguageSelect />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <SnackbarContent
          className={snackbarType === "error" ? "login-snackbar-error" : "login-snackbar-success"}
          message={snackbarMessage}
        />
      </Snackbar>
      <Grid className="forgot-password-child-div">
        {isLoading && dynamicLogo && (
          <Box
            component="img"
            id="login-img-learn"
            className="forgot-password-logo"
            src={dynamicLogo}
            alt="Learnify"
          />
        )}
      </Grid>
      {!isOnline && (
        <Box className={`form-group error-msg ${classes.onlineError}`}>
          {t("No internet connection!")}
        </Box>
      )}
      <Grid className="forgot-password-right-side-box">
        <Box className="form-group error-msg" id="reset-credential-error"></Box>
        <Box className="mdl-card__supporting-text input-fields">
          <Box className={`forget-title ${classes.forgotTitle}`}>
            {t("Forgot Password")}
          </Box>
          <Box>
            <Box component="p" className="custom-sub-title">
              {t(
                "Enter your email address or mobile number, and we’ll send you a verification code to reset your password."
              )}
            </Box>
          </Box>
          <Box
            component="form"
            id="userForgotPassForm"
            className="form-horizontal"
            onSubmit={handleForgotPassword}
          >
            {showCountryCode ? (
              <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <Box className="mdc-text-field mdc-text-field--outlined" sx={{ width: "150px" }}>
                  <InputLabel
                    className={`${classes.codeLabel} mdc-text-field__label`}
                  >
                    {t("Code")}
                  </InputLabel>
                  <Select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className={`${classes.selectFullWidth} input-box`}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem key={country.dialCode} value={country.dialCode}>
                        {country.display}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box className="mdc-text-field mdc-text-field--outlined" id="forgot-input" sx={{ flex: 1 }}>
                  <Input
                    type="text"
                    value={email}
                    className="input-box required_text"
                    id="userForgotPassEmail"
                    placeholder={t("Mobile No.")}
                    onChange={handleEmailChange}
                  />
                  <Box>
                    <InputLabel
                      htmlFor="userForgotPassEmail"
                      className={`${classes.inputLabelColor} mdc-text-field__label`}
                    >
                      {t("Mobile No.")}
                    </InputLabel>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                className="mdc-text-field mdc-text-field--outlined"
                id="forgot-input"
              >
                <Input
                  type="text"
                  value={email}
                  className="input-box required_text"
                  id="userForgotPassEmail"
                  placeholder={t("Email/Mobile No.")}
                  onChange={handleEmailChange}
                />
                <Box>
                  <InputLabel
                    htmlFor="userForgotPassEmail"
                    className={`${classes.inputLabelColor} mdc-text-field__label`}
                  >
                    {t("Email/Mobile No.")}
                  </InputLabel>
                </Box>
              </Box>
            )}
            <Box className="otg otg-items-1 text-center verify-proceed-button send-reset-link-div">
              <Button
                ref={changePasswordBtnRef}
                type="submit"
                id="changeCredentialBtn"
                className={`send-reset-link-button ${isButtonDisabled ? "disabled-btn" : ""
                  } ${classes.submitButton}`}
                disabled={isButtonDisabled}
              >
                {t("Send Verification Code")}
              </Button>
            </Box>
            <Box className="back-login">
              <Link
                component={RouterLink}
                to="/login"
                className={`back-login ${classes.backLoginLink}`}
              >
                {t("Back to Login")}
              </Link>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;